import { concatMap, delay, map, mergeMap, retryWhen, tap } from 'rxjs/operators';

import { AfterViewInit, Component, OnInit } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { Container } from '../container';
import { DisableSubmit } from '../utils/disable-submit';
import { Server } from '../server';
import { User } from '../user';
import { UserService } from '../user.service';
import { Observable, of, OperatorFunction, throwError } from 'rxjs';
import { Token } from '../token';
import { HttpErrorResponse } from '@angular/common/http';
import { FeatureFlagsService, Features } from '../feature-flags.service';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DescriptionService } from '../descriptions/description.service';
import { DescriptionData, NotebookNotebook } from './notebook-description';

@Component({
  selector: 'naas-container-selector',
  templateUrl: './container-selector.component.html',
  styleUrls: ['./container-selector.component.scss'],
})
export class ContainerSelectorComponent implements OnInit, AfterViewInit {
  submit = new DisableSubmit('Start', 'Starting...');
  shutdown = new DisableSubmit('Shut Down', 'Shutting Down...');
  user: User;
  containers: Container[] = [];
  server: Server | null;
  errorMessage?: string;
  connecting = false;
  progressText?: string;
  collabEnabled: boolean;
  form: UntypedFormGroup;
  descriptionData: DescriptionData | null = null;
  selectedNotebook: NotebookNotebook | null = null;

  constructor(
    private userService: UserService,
    private featureFlagService: FeatureFlagsService,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private http: HttpClient,
    private descriptionService: DescriptionService
  ) {}

  openDetails() {
    const description_modal = document.getElementById('description_modal') as HTMLDialogElement;
    description_modal.showModal();
    document.body.style.overflow = 'hidden'; // Prevent scrolling when the popup is open
  }

  closeDetails() {
    const description_modal = document.getElementById('description_modal') as HTMLDialogElement;
    description_modal.close();
    document.body.style.overflow = ''; // Restore scrolling when the popup is closed
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      select: this.updateContainerSelect(),
    });

    this.route.data.subscribe((data: { user: User; containers: Container[]; server: Server | null }) => {
      this.user = data.user;
      this.containers = data.containers.sort((x1, x2) => x1.display_name.localeCompare(x2.display_name));
      this.server = data.server;
      this.collabEnabled = this.featureFlagService.getFeatureFlag(Features.Collab) || false;

      // Keeps track of selections in the non-collab drop down and the collab drop down separately.
      // This makes the UI behave a bit nicer and remembers your selection in each different mode.
      this.form.patchValue({
        select: this.updateSelectedContainer(false).id.toString(),
      });

      // If the user refreshes the launch page during container start up, this will resume the
      // spinning start button and connect them automatically to their notebook.
      if (this.isServerSpawning()) {
        this.startServer();
      }

      this.descriptionService.getDescription().subscribe(data => {
        this.descriptionData = data;
        this.updateSelectedNotebookDescription();
      });

      // Update the selected notebook description when the select dropdown changes
      this.form.get('select')?.valueChanges.subscribe(() => {
        this.updateSelectedNotebookDescription();
      });
    });
  }

  ngAfterViewInit() {
    // Keeps track of selections in the non-collab drop down and the collab drop down separately.
    // This makes the UI behave a bit nicer and remembers your selection in each different mode.

    const selectDropdown = document.getElementById('containerSelect') as HTMLSelectElement;
    if (selectDropdown !== null && typeof selectDropdown !== 'undefined') {
      this.form.patchValue({
        select: this.updateSelectedContainer(false).id.toString(),
      });
    }
  }

  /**
   * Updates the description of the currently selected notebook based on the user's selection in the dropdown.
   * Retrieves the selected notebook ID from the form, finds the corresponding container, and then matches it
   * with the notebook entry to set the appropriate description. If no matching container or description data is found,
   * the method exits early without updating the description.
   */
  updateSelectedNotebookDescription() {
    const selectedId = parseInt(this.form.get('select')?.value, 10);
    if (!this.descriptionData) return;

    const selectedContainer = this.containers.find(c => c.id === selectedId);
    if (!selectedContainer) return;

    const matchedEntry = this.descriptionData.notebooks.find(
      (entry: NotebookNotebook) => entry.display_name === selectedContainer.display_name
    );

    this.selectedNotebook = matchedEntry ?? null;
  }

  isServerLive() {
    // Covers all other container states that are not spawning(pending) & terminating
    // If a container status is not null then something is running
    return (this.server?.status ?? '') == 'Running';
  }

  isServerSpawning() {
    return this.server ? this.server.status === 'Pending' || this.server.status === 'Unschedulable' : false;
  }

  isServerTerminating() {
    return this.server ? this.server.status === 'Terminating' : false;
  }

  showStartButtons() {
    return (!this.isServerLive() || this.isServerSpawning()) && !this.isServerTerminating();
  }

  isUserUpdating() {
    return this.user.is_updating;
  }

  isConnecting() {
    return this.connecting;
  }

  startServer() {
    this.submit.setSubmitting(true);
    this.connecting = true;

    const selectDropdown = document.getElementById('containerSelect') as HTMLSelectElement;
    const selectIndex = selectDropdown.selectedIndex;
    const selectOption = selectDropdown.options[selectIndex];

    return this.userService
      .startServer({ id: parseInt(selectOption.value, 10) }) //ParseInt is only to ensure correct typing. Nothing other than an int should ever be passed in here.
      .pipe(
        tap(() => (this.progressText = 'Starting Notebook Server...')),
        this.waitUntilStarted$(),
        tap(() => (this.progressText = 'Connecting to Notebook Server...')),
        this.waitUntilReady$(),
        tap(token => (window.location.href = this.getOpenNotebookUrl(token.token)))
      )
      .subscribe(
        () => this.onSuccess(),
        error => this.onError(error, 'start')
      );
  }

  reconnect() {
    this.connecting = true;
    this.userService
      .reconnect()
      .pipe(
        tap(res => {
          window.location.href = this.getOpenNotebookUrl(res.token);
        })
      )
      .subscribe();
  }

  stopServer() {
    this.shutdown.setSubmitting(true);

    return this.userService
      .stopServer()
      .pipe(
        tap(() => (this.progressText = 'Notebook Servers can take up to 30 seconds to shutdown')),
        this.waitUntilStopped$()
      )
      .subscribe(
        () => this.onSuccess(),
        error => this.onError(error, 'stop')
      );
  }

  updateContainerSelect() {
    try {
      return this.updateSelectedContainer(false).id.toString();
    } catch {
      return 0; //If no notebooks are found, default to 0
    }
  }

  updateSelectedContainer(collab: boolean): Container {
    const containers = this.containers.filter(c => c.is_collab || c.is_collab === collab);
    return (
      containers.find(c => c.display_name === this.user.notebook) ||
      containers.find(c => this.server !== null && c.container_name === this.server.latest_container) ||
      containers.find(c => c.display_name === 'Standard Notebook (Python 3)') ||
      containers[0]
    );
  }

  private getNotebookUrl(token: string): string {
    return '/user/' + this.user.uuid + '?token=' + token;
  }

  /**
   *
   * @param token
   * @private
   *
   * This is a hack to make the RStudio container start in RStudio rather than Lab
   * hopefully this can be removed in future and replaced with getNotebookUrl
   */
  private getOpenNotebookUrl(token: string): string {
    const name = this.server?.latest_container ?? '';
    if (name.includes('rstudio')) {
      return '/user/' + this.user.uuid + '/rstudio' + '?token=' + token;
    }
    return '/user/' + this.user.uuid + '?token=' + token;
  }

  /**
   * Starts a notebook/container and waits for the backend to say its running
   */
  private waitUntilStarted$(): OperatorFunction<Token, Token> {
    return mergeMap(token =>
      this.userService.getServerDetail().pipe(
        tap(server => {
          if (this.server && this.server.status === 'Unschedulable') {
            this.progressText =
              'Our servers are currently at full capacity. ' + 'Your Notebook Server will start as soon as possible.';
          } else {
            this.progressText = 'Starting Notebook Server...';
          }
          this.server = server;
        }),
        map(() => {
          const liveness = this.isServerLive();
          if (liveness === false || liveness === undefined || liveness === null) {
            throw new Error();
          }
          return token;
        }),
        retryWhen(errors => this.handleError(errors))
      )
    );
  }

  /**
   * Waits until the notebook/container accepts connections. This sometimes
   * takes some time due to the ingress resource needing to wait until the container
   * is fully running before it routes correctly. This server request will return a 500/502/504
   * error until the ingress controller is routing to the notebook. It will then return a 200
   * and when it does the users browser can safely be redirected there.
   */
  private waitUntilReady$(): OperatorFunction<Token, Token> {
    return mergeMap(token =>
      this.userService.testNotebookStatus(this.getNotebookUrl(token.token)).pipe(
        tap(response => {
          if (response.ok) {
            this.connecting = false;
            return;
          }
          throw new Error('Notebook Server not started yet');
        }),
        map(() => token),
        retryWhen(errors => errors.pipe(delay(1000))) // retry *ALL* errors, even 50x, 40x etc
      )
    );
  }

  /**
   * Waits until a notebook/container has shutdown
   */
  private waitUntilStopped$(): OperatorFunction<number, Server> {
    return mergeMap(() =>
      this.userService.getServerDetail().pipe(
        tap(server => (this.server = server)),
        map(server => {
          if (this.isServerTerminating()) {
            throw new Error();
          }
          return server;
        }),
        retryWhen(errors => this.handleError(errors))
      )
    );
  }

  /**
   * For HttpErrorResponses:
   * Handles retries for 20x messages but throws an error for anything else.
   * The API returns pending etc status for some operations so we retry them until
   * the pending goes away. If we get an HTTP error then we stop retrying and display the
   * error message.
   * For Errors (eg throw Error(...)):
   * If the message is blank we continue and retry, otherwise we display the error message.
   *
   * @param errors
   */
  private handleError(errors: Observable<HttpErrorResponse | Error>) {
    return errors.pipe(
      concatMap(e => {
        // Avoid error retries for Http status codes 40x & 50x
        // Eg, e must be a proper HttpErrorResponse with a non-200
        if (e instanceof HttpErrorResponse && !e.ok) {
          // Been an actual error so don't retry anymore, display an error
          return throwError(e);
        }
        if (e.message !== undefined && e.message !== '') {
          return throwError(e);
        }
        // Not an HTTP error so do the retry, eg we're still waiting on the container
        // This Error type is generated above with a throw
        return of(e);
      }),
      delay(2000)
    );
  }

  /**
   * Reset state when submit/shutdown is finished normally
   */
  private onSuccess() {
    this.errorMessage = undefined;
    this.submit.setSubmitting(false);
    this.shutdown.setSubmitting(false);
    this.progressText = undefined;
  }

  /**
   * Reset state/display an error when submit/shutdown fails for some reason
   *
   * @param error
   * @param verb
   */
  private onError(error: HttpErrorResponse | string, verb: 'start' | 'stop') {
    if (error instanceof HttpErrorResponse) {
      this.errorMessage =
        error.error !== undefined && error.error.message !== undefined
          ? error.error.message
          : `Unable to ${verb} your Notebook. Please refresh this page and try again later.`;
    } else {
      this.errorMessage = error;
    }
    this.submit.setSubmitting(false);
    this.shutdown.setSubmitting(false);
    this.progressText = undefined;
    this.server = null;
  }
}
